import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Settings, Check, X } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from "@/components/ui/switch";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { AVAILABLE_TOOLS } from './tools/ToolSelector';
import { Badge } from "@/components/ui/badge";

interface ToolsToggleSectionProps {
  enabledTools: string[];
  onToolsChange: (updatedTools: string[]) => void;
  readOnly?: boolean;
  userHasEditPermission?: boolean;
  sessionOnly?: boolean;
}

export function ToolsToggleSection({
  enabledTools = [],
  onToolsChange,
  readOnly = false,
  userHasEditPermission = false,
  sessionOnly = true
}: ToolsToggleSectionProps) {
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [localTools, setLocalTools] = useState<string[]>(enabledTools);
  
  // Update local tools when enabled tools change from props
  useEffect(() => {
    setLocalTools(enabledTools);
  }, [enabledTools]);
  
  // Get all tool objects for the dialog
  // Get tools from AVAILABLE_TOOLS but only include ones that are in enabledTools
  const allToolObjects = AVAILABLE_TOOLS
    .filter(tool => enabledTools.includes(tool.id))
    .map(tool => ({
      ...tool,
      enabled: localTools.includes(tool.id)
    }));

  // Handle tool toggle
  const handleToolToggle = (toolId: string) => {
    if (readOnly) return;
    
    const updatedTools = localTools.includes(toolId)
      ? localTools.filter(id => id !== toolId)
      : [...localTools, toolId];
    
    setLocalTools(updatedTools);
  };

  // Save changes
  const handleSaveChanges = () => {
    onToolsChange(localTools);
    setToolDialogOpen(false);
  };

  // Cancel changes
  const handleCancelChanges = () => {
    setLocalTools(enabledTools); // Reset to original state
    setToolDialogOpen(false);
  };

  // Count active tools
  const activeToolCount = enabledTools.length;

  return (
    <div className="mb-4 mt-2">
      <Button
        variant="outline"
        size="sm"
        className="w-full text-xs justify-between group"
        onClick={() => setToolDialogOpen(true)}
      >
        <span className="flex items-center">
          <Settings className="h-3 w-3 mr-2" />
          {activeToolCount} {activeToolCount === 1 ? 'Tool' : 'Tools'} Enabled
        </span>
        {userHasEditPermission && !readOnly ? (
          <span className="text-primary opacity-0 group-hover:opacity-100 transition-opacity">
            Configure
          </span>
        ) : (
          <span>Details</span>
        )}
      </Button>

      {/* Tools Configuration Dialog */}
      <Dialog open={toolDialogOpen} onOpenChange={setToolDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {readOnly || !userHasEditPermission 
                ? `Assistant Tools (${enabledTools.length})` 
                : "Configure Tools"}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 py-2">
              {allToolObjects.length > 0 ? (
                allToolObjects.map(tool => (
                  <div 
                    key={tool.id} 
                    className={`flex items-start justify-between p-3 rounded-lg border ${
                      tool.enabled ? 'bg-accent/30 border-accent' : 'bg-card border-border'
                    }`}
                  >
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <tool.icon className={`h-5 w-5 ${tool.enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                        <h4 className="font-medium flex items-center gap-2">
                          {tool.name}
                          {tool.enabled && (
                            <Badge variant="outline" className="ml-1 text-xs py-0">Active</Badge>
                          )}
                        </h4>
                      </div>
                      <p className="text-sm text-muted-foreground pl-7">
                        {tool.description}
                      </p>
                    </div>
                    
                    {!readOnly && userHasEditPermission ? (
                      <Switch
                        checked={tool.enabled}
                        onCheckedChange={() => handleToolToggle(tool.id)}
                        className="ml-2 mt-1"
                      />
                    ) : tool.enabled ? (
                      <Check className="h-4 w-4 text-primary mt-1" />
                    ) : null}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tools available for this assistant.
                </p>
              )}
            </div>
          </ScrollArea>
          
          {/* Dialog Footer with Save/Cancel buttons when editable */}
          {!readOnly && userHasEditPermission && (
            <DialogFooter className="flex justify-between gap-2 pt-4 border-t">
              <div className="flex items-center text-sm text-muted-foreground">
                <span>
                  {localTools.length} of {AVAILABLE_TOOLS.length} tools selected
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelChanges}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveChanges}
                >
                  Save Changes
                </Button>
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ToolsToggleSection;